/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Wallet & Immutable Credit Ledger Zod Schemas & DTOs
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Workspace Wallet representation schema.
 */
export const WalletSchema = z
  .object({
    workspaceId: z.string().openapi({ description: 'Workspace unique identifier', example: 'ws_01HXYZ123' }),
    balance: z.number().openapi({ description: 'Current available credit balance', example: 50000 }),
    reservedBalance: z.number().openapi({ description: 'Credits currently reserved for in-flight tasks', example: 0 }),
    lifetimeCredits: z.number().openapi({ description: 'All-time cumulative credited amount', example: 100000 }),
    updatedAt: z.number().openapi({ description: 'Unix timestamp of last ledger modification', example: 1729340000 }),
  })
  .openapi('Wallet');

export type WalletDto = z.infer<typeof WalletSchema>;

/**
 * Immutable Credit Ledger Entry representation schema.
 */
export const LedgerEntrySchema = z
  .object({
    id: z.string().openapi({ description: 'Ledger entry immutable transaction ID', example: 'tx_01HXYZ123' }),
    workspaceId: z.string().openapi({ description: 'Workspace ID', example: 'ws_01HXYZ123' }),
    userId: z.string().nullable().openapi({ description: 'Actor User ID', example: 'usr_01HXYZ789' }),
    apiKeyId: z.string().nullable().openapi({ description: 'Calling API Key ID if automated', example: null }),
    amount: z.number().openapi({ description: 'Credit delta (+ for top-up, - for AI usage)', example: -50 }),
    balanceAfter: z.number().openapi({ description: 'Snapshot balance after this mutation', example: 49950 }),
    reason: z.string().openapi({ description: 'Auditable operation reason', example: 'AI_TEXT_GENERATION' }),
    referenceId: z.string().nullable().openapi({ description: 'Related entity ID (Job, Order, Session)', example: 'job_txt_01' }),
    modelCode: z.string().nullable().openapi({ description: 'AI Model used', example: 'gpt-4o' }),
    tokensUsed: z.number().nullable().openapi({ description: 'Total LLM tokens or media units', example: 1250 }),
    createdAt: z.number().openapi({ description: 'Unix timestamp of transaction', example: 1729340000 }),
  })
  .openapi('LedgerEntry');

export type LedgerEntryDto = z.infer<typeof LedgerEntrySchema>;

/**
 * Query schema for listing credit ledger entries with pagination & filters.
 */
export const LedgerQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1).openapi({ description: 'Page number', example: 1 }),
    limit: z.coerce.number().int().min(1).max(100).default(20).openapi({ description: 'Items per page', example: 20 }),
    reason: z.string().optional().openapi({ description: 'Filter by transaction reason', example: 'AI_TEXT_GENERATION' }),
    referenceId: z.string().optional().openapi({ description: 'Filter by reference job/order ID', example: 'job_txt_01' }),
    from: z.coerce.number().optional().openapi({ description: 'Filter created after Unix timestamp', example: 1729300000 }),
    to: z.coerce.number().optional().openapi({ description: 'Filter created before Unix timestamp', example: 1729350000 }),
  })
  .openapi('LedgerQueryRequest');

export type LedgerQueryDto = z.infer<typeof LedgerQuerySchema>;

/**
 * Schema for credit deduction operation.
 */
export const DeductCreditsSchema = z
  .object({
    amount: z.number().positive('Amount must be greater than zero').openapi({ description: 'Credits to deduct', example: 50 }),
    reason: z.string().trim().min(1, 'Reason is required').openapi({ description: 'Reason for deduction', example: 'AI_TEXT_GENERATION' }),
    referenceId: z.string().optional().openapi({ description: 'Job or resource ID', example: 'job_txt_01' }),
    idempotencyKey: z.string().optional().openapi({ description: 'Unique idempotency key to prevent double deduction', example: 'k6_tx_001' }),
    modelCode: z.string().optional().openapi({ description: 'AI Model code', example: 'claude-3-5-sonnet' }),
    tokensUsed: z.number().int().nonnegative().optional().openapi({ description: 'Actual tokens used', example: 2500 }),
  })
  .openapi('DeductCreditsRequest');

export type DeductCreditsDto = z.infer<typeof DeductCreditsSchema>;

/**
 * Schema for credit grant / top-up operation.
 */
export const GrantCreditsSchema = z
  .object({
    amount: z.number().positive('Amount must be greater than zero').openapi({ description: 'Credits to grant', example: 10000 }),
    reason: z.string().trim().min(1, 'Reason is required').openapi({ description: 'Reason for grant', example: 'TOPUP_PURCHASE' }),
    referenceId: z.string().optional().openapi({ description: 'Order ID or payment intent ID', example: 'ord_stripe_999' }),
  })
  .openapi('GrantCreditsRequest');

export type GrantCreditsDto = z.infer<typeof GrantCreditsSchema>;

